import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, BedDouble, Users, Sparkles, AlertOctagon, CalendarRange } from "lucide-react";
import api from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import EmptyState from "@/components/EmptyState";
import {
    PROPERTY_STATE_LABELS,
    TASK_STATE_LABELS,
    INCIDENT_STATE_LABELS,
    TASK_TYPE_LABELS,
    INCIDENT_TYPE_LABELS,
    PRIORITY_LABELS,
} from "@/lib/dictionaries";

const SOURCE_LABELS = {
    airbnb: "Airbnb",
    booking: "Booking.com",
    vrbo: "Vrbo",
    avaibook: "Avaibook",
    avantio: "Avantio",
    manual: "Manual",
    other: "Otro",
};

export default function PortalPropertyDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("upcoming");

    useEffect(() => {
        let active = true;
        api.get(`/portal/properties/${id}`)
            .then((r) => {
                if (active) setData(r.data);
            })
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [id]);

    if (loading || !data) return <div className="zentra-skeleton h-72" data-testid="portal-detail-loading" />;
    const { property: p, tasks = [], incidents = [], bookings = [] } = data;
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = tasks.filter((t) => (t.scheduled_date || "") >= today).sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
    const history = tasks.filter((t) => (t.scheduled_date || "") < today).sort((a, b) => b.scheduled_date.localeCompare(a.scheduled_date));

    return (
        <div className="space-y-6" data-testid="portal-property-detail">
            <div>
                <Link to="/portal/propiedades" className="text-xs text-zentra-muted hover:text-zentra-ink inline-flex items-center gap-1.5">
                    <ArrowLeft size={12} /> Volver al listado
                </Link>
            </div>

            <div className="zentra-card-static p-6 flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                    <p className="text-[11px] font-mono-zentra tracking-wider text-zentra-muted uppercase">{p.code}</p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        {p.name}
                    </h2>
                    <p className="text-sm text-zentra-muted mt-2 inline-flex items-center gap-1.5">
                        <MapPin size={13} /> {p.address || "Sin dirección"}
                    </p>
                    <div className="flex items-center gap-4 mt-4 text-xs text-zentra-muted">
                        <span className="inline-flex items-center gap-1.5">
                            <BedDouble size={12} /> {p.bedrooms || 0} habitaciones · {p.bathrooms || 0} baños
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Users size={12} /> {p.capacity || 0} pax
                        </span>
                    </div>
                </div>
                <StateBadge value={p.state} dict={PROPERTY_STATE_LABELS} />
            </div>

            {/* tabs */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="portal-tabs">
                <TabButton active={tab === "upcoming"} onClick={() => setTab("upcoming")} icon={Sparkles} label={`Próximas (${upcoming.length})`} testId="tab-upcoming" />
                <TabButton active={tab === "history"} onClick={() => setTab("history")} icon={Sparkles} label={`Historial (${history.length})`} testId="tab-history" />
                <TabButton active={tab === "incidents"} onClick={() => setTab("incidents")} icon={AlertOctagon} label={`Incidencias (${incidents.length})`} testId="tab-incidents" />
                <TabButton active={tab === "bookings"} onClick={() => setTab("bookings")} icon={CalendarRange} label={`Reservas (${bookings.length})`} testId="tab-bookings" />
            </div>

            <div className="zentra-card-static overflow-hidden">
                {tab === "upcoming" && (
                    upcoming.length === 0 ? (
                        <EmptyState icon={Sparkles} title="Sin limpiezas próximas" description="No hay servicios planificados todavía." />
                    ) : (
                        <table className="zentra-table" data-testid="portal-upcoming-table">
                            <thead><tr><th>Fecha</th><th>Tipo</th><th>Prioridad</th><th>Estado</th><th>Notas</th></tr></thead>
                            <tbody>
                                {upcoming.map((t) => (
                                    <tr key={t.id}>
                                        <td className="font-mono-zentra text-xs">{t.scheduled_date}</td>
                                        <td>{TASK_TYPE_LABELS[t.type] || t.type}</td>
                                        <td><StateBadge value={t.priority} dict={PRIORITY_LABELS} /></td>
                                        <td><StateBadge value={t.state} dict={TASK_STATE_LABELS} /></td>
                                        <td className="text-xs text-zentra-muted">{t.notes || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                )}
                {tab === "history" && (
                    history.length === 0 ? (
                        <EmptyState icon={Sparkles} title="Sin historial" description="Aún no hay servicios completados." />
                    ) : (
                        <table className="zentra-table">
                            <thead><tr><th>Fecha</th><th>Tipo</th><th>Estado</th><th>Completada</th></tr></thead>
                            <tbody>
                                {history.map((t) => (
                                    <tr key={t.id}>
                                        <td className="font-mono-zentra text-xs">{t.scheduled_date}</td>
                                        <td>{TASK_TYPE_LABELS[t.type] || t.type}</td>
                                        <td><StateBadge value={t.state} dict={TASK_STATE_LABELS} /></td>
                                        <td className="text-xs text-zentra-muted">{t.completed_at ? new Date(t.completed_at).toLocaleString("es-ES") : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                )}
                {tab === "incidents" && (
                    incidents.length === 0 ? (
                        <EmptyState icon={AlertOctagon} title="Sin incidencias" description="No hay incidencias registradas para esta propiedad." />
                    ) : (
                        <table className="zentra-table">
                            <thead><tr><th>Título</th><th>Tipo</th><th>Prioridad</th><th>Estado</th><th>Creada</th></tr></thead>
                            <tbody>
                                {incidents.map((i) => (
                                    <tr key={i.id}>
                                        <td className="font-medium">{i.title}</td>
                                        <td>{INCIDENT_TYPE_LABELS[i.type] || i.type}</td>
                                        <td><StateBadge value={i.priority} dict={PRIORITY_LABELS} /></td>
                                        <td><StateBadge value={i.state} dict={INCIDENT_STATE_LABELS} /></td>
                                        <td className="text-xs text-zentra-muted font-mono-zentra">{(i.created_at || "").slice(0, 10)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                )}
                {tab === "bookings" && (
                    bookings.length === 0 ? (
                        <EmptyState icon={CalendarRange} title="Sin reservas" description="No hay reservas sincronizadas." />
                    ) : (
                        <table className="zentra-table">
                            <thead><tr><th>Check-in</th><th>Check-out</th><th>Huésped</th><th>Canal</th></tr></thead>
                            <tbody>
                                {bookings.map((b) => (
                                    <tr key={b.id}>
                                        <td className="font-mono-zentra text-xs">{b.checkin}</td>
                                        <td className="font-mono-zentra text-xs">{b.checkout}</td>
                                        <td>{b.guest_name || "—"}</td>
                                        <td className="text-xs text-zentra-muted">{SOURCE_LABELS[b.source] || b.source}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                )}
            </div>
        </div>
    );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition border ${
                active
                    ? "bg-zentra-primary text-white border-zentra-primary"
                    : "bg-white text-zentra-ink border-zentra-border hover:border-zentra-olive"
            }`}
        >
            <Icon size={12} /> {label}
        </button>
    );
}
